import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, CheckCircle2, Award, Code2, Rocket, FlaskConical, Briefcase, GraduationCap, Trash2, X, Loader2 } from "lucide-react";

const TYPES = [
  { id: "Skill Acquired", icon: GraduationCap, color: "#6366F1" },
  { id: "Certification", icon: Award, color: "#22C55E" },
  { id: "Project", icon: Code2, color: "#0EA5E9" },
  { id: "Hackathon", icon: Rocket, color: "#F59E0B" },
  { id: "Research", icon: FlaskConical, color: "#A855F7" },
  { id: "Internship", icon: Briefcase, color: "#EC4899" },
];

const iconFor = (t) => TYPES.find((x) => x.id === t) || TYPES[1];

export default function Timeline() {
  const { refreshUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ activity_type: "Certification", title: "", date_acquired: "", skill_id: null, skill_level: null });
  const [skillQuery, setSkillQuery] = useState("");
  const [skillResults, setSkillResults] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/timeline");
    setEvents(data.events);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!skillQuery) { setSkillResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/skills", { params: { search: skillQuery, limit: 6 } });
      setSkillResults(data.skills);
    }, 250);
    return () => clearTimeout(t);
  }, [skillQuery]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (selectedSkill) { payload.skill_id = selectedSkill.id; if (!payload.skill_level) payload.skill_level = "basic"; }
      await api.post("/timeline", payload);
      toast.success("Added to your Digital Twin!");
      setOpen(false);
      setForm({ activity_type: "Certification", title: "", date_acquired: "", skill_id: null, skill_level: null });
      setSelectedSkill(null); setSkillQuery("");
      await refreshUser();
      await load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/timeline/${id}`); await refreshUser(); await load(); toast.success("Removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow text-slate-400">CAREER DIGITAL TWIN</p>
          <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mt-1">Your verified journey</h1>
        </div>
        <button data-testid="add-event-btn" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
          <Plus size={18} /> Add milestone
        </button>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 border border-dashed border-slate-300 text-center">
          <p className="text-slate-500">Your timeline is empty. Add your first skill, certification or project.</p>
        </div>
      ) : (
        <div className="relative pl-8">
          <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-700" />
          <div className="space-y-4">
            {events.map((ev) => {
              const meta = iconFor(ev.Activity_Type);
              const Icon = meta.icon;
              return (
                <div key={ev.id} data-testid={`timeline-event-${ev.id}`} className="relative animate-fade-up">
                  <div className="absolute -left-8 top-4 h-6 w-6 rounded-full flex items-center justify-center ring-4 ring-[#0F172A]"
                    style={{ backgroundColor: meta.color }}>
                    <Icon size={13} className="text-white" />
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200 group">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
                            {ev.Activity_Type}
                          </span>
                          {ev.Verification_Status === "Verified" && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-green-600 font-medium"><CheckCircle2 size={12} /> Verified</span>
                          )}
                        </div>
                        <p className="font-semibold text-slate-900 mt-1.5">{ev.Title}</p>
                        <p className="text-xs text-slate-400 font-mono mt-1">{ev.Date_Acquired} · +{ev.XP_Earned} XP</p>
                      </div>
                      <button onClick={() => remove(ev.id)} data-testid={`delete-event-${ev.id}`}
                        className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-event-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg text-slate-900 tracking-tight">Add milestone</h2>
              <button onClick={() => setOpen(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Type</label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {TYPES.map((t) => (
                    <button key={t.id} type="button" data-testid={`type-${t.id}`} onClick={() => setForm({ ...form, activity_type: t.id })}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-medium transition-colors ${
                        form.activity_type === t.id ? "border-[#6366F1] bg-[#6366F1]/5 text-[#4F46E5]" : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}>
                      <t.icon size={16} /> {t.id.split(" ")[0]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Title</label>
                <input data-testid="event-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. AWS Cloud Practitioner"
                  className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date</label>
                <input data-testid="event-date" type="date" value={form.date_acquired} onChange={(e) => setForm({ ...form, date_acquired: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Link a skill (optional — boosts readiness)</label>
                {selectedSkill ? (
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 bg-[#6366F1] text-white text-sm px-3 py-1.5 rounded-full">
                      {selectedSkill.Skill_Name} <button type="button" onClick={() => setSelectedSkill(null)}><X size={13} /></button>
                    </span>
                    <select data-testid="skill-level-select" value={form.skill_level || "basic"} onChange={(e) => setForm({ ...form, skill_level: e.target.value })}
                      className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
                      <option value="basic">Basic</option>
                      <option value="production">Production Ready</option>
                    </select>
                  </div>
                ) : (
                  <>
                    <input data-testid="skill-search" value={skillQuery} onChange={(e) => setSkillQuery(e.target.value)} placeholder="Search skills..."
                      className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 focus:border-[#6366F1] outline-none transition-colors" />
                    {skillResults.length > 0 && (
                      <div className="mt-1 border border-slate-200 rounded-lg max-h-36 overflow-y-auto">
                        {skillResults.map((s) => (
                          <button key={s.id} type="button" data-testid={`skill-opt-${s.id}`} onClick={() => { setSelectedSkill(s); setSkillResults([]); setSkillQuery(""); }}
                            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">{s.Skill_Name}</button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <button data-testid="save-event-btn" disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                {saving && <Loader2 size={16} className="animate-spin" />} Add to timeline
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
